import {
  FC,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import type { ActiveUser } from "@ingka-group-digital/cwds-layout-models";
import { NavItemModel } from "@ingka-group-digital/cwds-layout-models";
import { AppSwitcherDrawer } from "@ingka-group-digital/cwds-react-app-switcher";
import {
  Header as CWDSHeader,
  HeaderButton,
} from "@ingka-group-digital/cwds-react-header";
import { NavMenuDrawer } from "@ingka-group-digital/cwds-react-nav-menu";
import { UserProfileDrawer } from "@ingka-group-digital/cwds-react-user-profile";
import ListView, { ListViewItem } from "@ingka/list-view";
import Modal, { ModalHeader, Sheets } from "@ingka/modal";
import QuestionMarkIcon from "@ingka/ssr-icon/paths/question-mark-circle.js";
import {
  Environments,
  NOW_IT_LINK,
  getAccessibleApps,
  getOppCookie,
  getOppURL,
  isOpp,
} from "@pdpp/lib-react";

import { css, cx } from "../__generated-styled-system/css";
import { Auth } from "../api/auth";
import { getSupportedLanguage } from "../language/language";
import { ENVIRONMENT } from "../utils/runtimeConfig";
import { setShareableSetting } from "../utils/shareableSetting";
import { Language } from "./Language";

const PROJECT_NAME = "Workload planning";
const NAV_MENU_BREAKPOINT = 760;
const USER_MANUAL_URL =
  "https://iweof.sharepoint.com/:b:/r/sites/ms/rwm/hr/globalpeopleplanninghub/Shared%20Documents/Workload%20planning/1.%20User%20manual/Workload%20Planning%20User%20Manual.pdf?d=w821e23190eff48f1b6568801c1e5607d&csf=1&web=1&e=qSGBSY";
const IS_PROD = ENVIRONMENT.startsWith("prod");
const FALLBACK_PHOTO =
  "data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.672 6.468c.274-.281.68-.47 1.35-.47.397 0 .705.083.953.222.24.134.437.33.593.619l.003.006c.14.254.237.618.237 1.14 0 .786-.204 1.245-.48 1.528l-.004.005c-.276.288-.68.476-1.334.476-.386 0-.696-.082-.956-.224a1.49 1.49 0 0 1-.589-.614c-.145-.275-.242-.652-.242-1.17 0-.791.204-1.245.47-1.518zm1.35-2.472c-1.084 0-2.054.326-2.784 1.077-.738.758-1.036 1.78-1.036 2.912 0 .766.143 1.483.477 2.112l.003.006a3.488 3.488 0 0 0 1.378 1.415l.006.004a3.94 3.94 0 0 0 1.924.474c1.082 0 2.048-.334 2.775-1.09.741-.763 1.044-1.786 1.044-2.92 0-.764-.142-1.48-.482-2.1a3.488 3.488 0 0 0-1.376-1.413c-.585-.327-1.238-.477-1.93-.477zM6.469 16.858a3.25 3.25 0 0 1 1.29-1.357c.558-.32 1.283-.505 2.221-.505h4.04c.938 0 1.663.186 2.22.505.568.33.993.777 1.29 1.357.304.59.47 1.294.47 2.138v1h2v-1c0-1.112-.22-2.138-.69-3.052a5.322 5.322 0 0 0-2.073-2.177c-.927-.532-2.015-.77-3.217-.77H9.98c-1.115 0-2.248.211-3.223.773a5.25 5.25 0 0 0-2.067 2.174c-.47.914-.69 1.94-.69 3.052v1h2v-1c0-.844.166-1.548.47-2.138z' /%3E%3C/svg%3E";

const supportItemStyles = css({
  whiteSpace: "pre-wrap",
});

/**
 * Temporary fix for Firefox
 * so that the header's content
 * is not centered:
 * https://jira.digital.ingka.com/servicedesk/customer/portal/409/ICWDS-427
 */
const layoutStyles = css({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  overflow: "hidden",
  "& .cwds-header__container": {
    width: "100%",
    flexShrink: 0,
  },
  /* Fallback user avatar in a white circle */
  "& .cwds-header__container [class*='avatar'] img": {
    borderRadius: "50%",
    backgroundColor: "white",
    objectFit: "contain",
    boxSizing: "border-box",
  },
});

const contentAreaStyles = css({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  paddingTop: "space150!",
});

const layoutDevStyles = css({
  "& .cwds-header__container": {
    backgroundImage: `url('data:image/svg+xml;utf8,<svg style="transform:rotate(45deg)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 40"><text x="0" y="25" fill="%23fff" fill-opacity="0.2" font-family="Noto IKEA, sans-serif" font-size="0.7rem">dev</text></svg>')
    `,
  },
});

const layoutStageStyles = css({
  "& .cwds-header__container": {
    backgroundImage: `url('data:image/svg+xml;utf8,<svg style="transform:rotate(45deg)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 40"><text x="0" y="25" fill="%23fff" fill-opacity="0.2" font-family="Noto IKEA, sans-serif" font-size="0.7rem">stage</text></svg>')`,
  },
});

const getLayoutStyles = ():
  | typeof layoutDevStyles
  | typeof layoutStageStyles
  | undefined => {
  if (ENVIRONMENT.includes("dev")) {
    return layoutDevStyles;
  } else if (ENVIRONMENT.startsWith("stage")) {
    return layoutStageStyles;
  }
  return undefined;
};

export type NavItemWithClick = NavItemModel & {
  readonly onClick: VoidFunction;
};

enum Support {
  default = "Default",
  raiseTicket = "RaiseTicket",
}

interface AppLayoutProps {
  readonly children: ReactNode;
  readonly navItems: Array<NavItemWithClick>;
}

type HeaderNavMode = "HEADER_LINKS" | "NAVIGATION_MENU";

export const AppLayout: FC<AppLayoutProps> = ({ navItems, children }) => {
  const [t] = useTranslation();
  const [supportContent, setSupportContent] = useState<Support | null>(null);
  const [user, setUser] = useState<ActiveUser | undefined>(undefined);
  const [isAppSwitcherShown, setIsAppSwitcherShown] = useState(false);
  const [isUserProfileShown, setIsUserProfileShown] = useState(false);
  const [isNavMenuShown, setIsNavMenuShown] = useState(false);
  const [headerNavMode, setHeaderNavMode] =
    useState<HeaderNavMode>("HEADER_LINKS");

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${NAV_MENU_BREAKPOINT - 1}px)`);
    const handleChange = (e: MediaQueryListEvent): void => {
      setHeaderNavMode(e.matches ? "NAVIGATION_MENU" : "HEADER_LINKS");
    };
    setHeaderNavMode(mql.matches ? "NAVIGATION_MENU" : "HEADER_LINKS");
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    Auth.get()
      .getUser()
      .then((p) => {
        if (p === undefined) {
          return;
        }
        const pp = p.profile;
        setUser({
          country: pp.country,
          given_name: pp.givenName,
          family_name: pp.surname,
          email: pp.mail,
          jobTitle: pp.jobTitle,
          businessUnit: pp.officeLocation,
          name: pp.displayName,
          picture: p.photo ?? FALLBACK_PHOTO,
        });
      });
  }, []);

  const fullHref = window.location.href;
  const env: Environments =
    fullHref.includes("dev") || fullHref.includes("localhost")
      ? "dev"
      : fullHref.includes("stage")
        ? "stage"
        : "prod";

  const isVersion2 = fullHref.includes("version=2");

  const accessibleApps = useMemo(() => {
    return getAccessibleApps("Workload planning", isVersion2, env);
  }, [env, isVersion2]);
  const isOppHeader = useMemo(() => isOpp(), []);

  const appSwitcherLabels = useMemo(
    () => ({
      goToHome: t("nav.goToHome", {
        home: "One people planning",
      }),
      title: t("nav.appSwitcherTitle", {
        project: "People planning",
      }),
    }),
    [t],
  );

  const userProfileLabels = useMemo(
    () => ({
      signOut: t("nav.signOut"),
    }),
    [t],
  );

  const sections = useMemo(
    () => [
      {
        title: t("language.preferredLanguage"),
        description: getSupportedLanguage(getOppCookie().lang),
        template: <Language />,
      },
    ],
    [t],
  );

  const utilities = useMemo(
    () => (
      <HeaderButton
        ssrIcon={QuestionMarkIcon}
        onClick={() => {
          setSupportContent(Support.default);
        }}
        data-analytics="support"
        size="small"
      />
    ),
    [],
  );

  const handleSupportBackBtnClick = useMemo(
    () =>
      supportContent === Support.raiseTicket
        ? () => setSupportContent(Support.default)
        : undefined,
    [supportContent],
  );

  const handleRedirectChange = useCallback(
    (href: string, _target: string, event: SyntheticEvent) => {
      const match = navItems.find((item) => item.href === href);

      if (match !== undefined) {
        event.preventDefault();
        match.onClick();
      }
    },
    [navItems],
  );

  const handleLogout = useCallback(
    () =>
      Auth.get().logout({
        postLogoutRedirectUri: "/",
      }),
    [],
  );

  const handleSupportClick = useCallback(
    () => setSupportContent(Support.raiseTicket),
    [],
  );
  const closeSupportDialog = useCallback(() => setSupportContent(null), []);

  const handleTitleClick = useCallback(() => {
    setShareableSetting("orgNode", "-1");
    window.location.reload();
  }, []);

  const handleHomeClick = useCallback(() => {
    window.location.href = getOppURL(IS_PROD);
  }, []);

  const setVisibleAppSwitcher = useCallback(() => {
    setIsAppSwitcherShown(true);
  }, []);

  const setVisibleUserProfile = useCallback(() => {
    setIsUserProfileShown(true);
  }, []);

  const setVisibleNavMenu = useCallback((visible: boolean) => {
    setIsNavMenuShown(visible);
  }, []);

  const handleNavMenuClose = useCallback(() => {
    setIsNavMenuShown(false);
  }, []);

  return (
    <>
      <div className={cx(layoutStyles, getLayoutStyles())}>
        <CWDSHeader
          narrowContent={false}
          user={user}
          title={PROJECT_NAME}
          headerNavMode={headerNavMode}
          isAppSwitcherShown={isOppHeader}
          isShowingBottomNav={false}
          setVisibleNavMenu={setVisibleNavMenu}
          setVisibleAppSwitcher={setVisibleAppSwitcher}
          setVisibleUserProfile={setVisibleUserProfile}
          utilities={utilities}
          onTitleClick={handleTitleClick}
          onRedirectChange={handleRedirectChange}
          navItems={navItems}
        />
        <div className={contentAreaStyles}>{children}</div>
      </div>

      {isNavMenuShown && (
        <NavMenuDrawer
          title={PROJECT_NAME}
          navItems={navItems}
          visible={true}
          prefix=""
          onNavMenuClose={handleNavMenuClose}
          onRedirectChange={handleRedirectChange}
        />
      )}

      {isOppHeader && isAppSwitcherShown && (
        <AppSwitcherDrawer
          prefix=""
          visible={true}
          accessibleApps={accessibleApps}
          isSeeAllAppsShown={false}
          dir="ltr"
          appSwithcerlabels={appSwitcherLabels}
          onHomeClick={handleHomeClick}
          onAppSwitcherClose={() => setIsAppSwitcherShown(false)}
        />
      )}

      {isUserProfileShown && user !== undefined && (
        <UserProfileDrawer
          user={user}
          sections={sections}
          languagePicker={null}
          logoutCallback={handleLogout}
          visible={true}
          prefix=""
          userProfileSectionListMode="LARGE"
          onUserProfileClose={() => setIsUserProfileShown(false)}
          userProfileLabels={userProfileLabels}
        />
      )}

      <Modal
        handleCloseBtn={closeSupportDialog}
        visible={supportContent !== null}
      >
        <Sheets
          header={
            <ModalHeader
              title={t("support.title")}
              floating={false}
              backBtnClick={handleSupportBackBtnClick}
            />
          }
          footer={null}
          alignment={"right"}
          size="small"
        >
          <>
            {supportContent === Support.default && (
              <ListView id="appLayout">
                <ListViewItem
                  title={t("support.manual.title", {
                    project: PROJECT_NAME,
                  })}
                  description={t("support.manual.description")}
                  emphasised={true}
                  href={USER_MANUAL_URL}
                  control="link"
                  controlIcon="linkOut"
                  newWindow="_blank"
                  rel="noopener noreferrer nofollow"
                  inset={true}
                  data-analytics="user-manual-link"
                />
                <ListViewItem
                  title={t("support.title")}
                  control="navigational"
                  controlIcon="chevronRightSmall"
                  inset={true}
                  emphasised={true}
                  onClick={handleSupportClick}
                  data-analytics="support/options"
                />
              </ListView>
            )}
            {supportContent === Support.raiseTicket && (
              <ListView id="support">
                <ListViewItem
                  className={supportItemStyles}
                  title={t("support.raiseTicket.title")}
                  description={t("support.raiseTicket.description", {
                    project: PROJECT_NAME,
                    areas: "HR tools / Property / Learning tools",
                  })}
                  control="link"
                  controlIcon="linkOut"
                  inset
                  emphasised
                  href={NOW_IT_LINK}
                  newWindow="_blank"
                  data-analytics="support/options/raise-ticket"
                />
              </ListView>
            )}
          </>
        </Sheets>
      </Modal>
    </>
  );
};
